/**
 * What answering an absence request does, beside the review bar's Innvilg and Avslå (review of
 * #147, F2; one line of chips since the owner's test of 18.10): the deduction, the balance before
 * and after, the company's limit warnings, and the person's shifts in the period. The same reads the
 * Fravær drawer makes (AbsenceDrawer.tsx, Saldo og påvirkning), so the bar never says less than the
 * page the decision used to live on; a chip's longer sentence is its tooltip and its description. A
 * read that has not answered says so; it is never drawn as a check that found nothing. None of them
 * holds the answer back: limits and balance are warnings, and the server approves regardless
 * (owner, 2026-09-18). Only the deduction holds Innvilg, as on Fravær.
 */
import { type ApiShift, useAbsenceLimits, useShifts, useUser } from '@/api/data';
import { T } from '@/i18n';
import { oslo } from '@/rules';
import { Tip } from '@/ui';
import { fmtD } from '@/ui/format';
import * as Popover from '@radix-ui/react-popover';
import { addDays } from '@timefly/core';
import { type ReactNode, useId } from 'react';
import { useDeductionState } from '../fravaer/Deduction';
import { absenceSpan, overlaps } from '../oversikt/cover';
import css from './ReviewBar.module.css';
import type { ReviewCase } from './reviewBar.types';

const dateOf = (s: ApiShift) => oslo(s.start_time)?.date ?? '';
const clock = (iso: string) => oslo(iso)?.time ?? '';
const days = (n: number) => (n === 1 || n === -1 ? T('{n} dag', { n }) : T('{n} dager', { n }));
/** «1 → −1 dag»: the unit once, a real minus sign */
const fromTo = (a: number, b: number) => `${a} → ${days(b).replace(/^-/, '−')}`;

type AbsenceCase = ReviewCase & { absence: NonNullable<ReviewCase['absence']> };

/**
 * One chip: a label, a value, and the sentence behind it as its tooltip and its description, so a
 * screen reader gets what the eye reads on hover. `retry` asks a failed read again.
 */
function Chip({
  testid,
  label,
  value,
  about,
  warn,
  retry,
}: {
  testid: string;
  label?: string;
  value: ReactNode;
  about?: string | undefined;
  warn?: boolean;
  retry?: () => void;
}) {
  const id = useId();
  const body = (
    // focus shows the sentence behind the chip
    <span
      className={`${css.chip} ${warn ? css.warn : ''}`}
      data-testid={testid}
      tabIndex={about ? 0 : undefined}
      aria-describedby={about ? id : undefined}
      role={warn ? 'status' : undefined}
    >
      {label && <span>{label}</span>}
      {value}
      {about && (
        <span id={id} hidden>
          {about}
        </span>
      )}
      {retry && (
        <button type="button" className={css.retry} onClick={retry}>
          {T('Prøv igjen')}
        </button>
      )}
    </span>
  );
  return about ? <Tip text={about}>{body}</Tip> : body;
}

/** The person's shifts the absence overlaps, a night that runs into its first day included. */
function useTheirShifts(c: AbsenceCase) {
  const a = c.absence;
  const from = oslo(a.start)?.date ?? '';
  const to = oslo(a.end)?.date ?? from;
  // read a day either side, then the same span Oversikt's cover check uses (oversikt/cover.ts)
  const span = absenceSpan({ start_date: a.start, end_date: a.end }, a.kind);
  const q = useShifts(addDays(from, -1), addDays(to, 1), !!from);
  const theirs = (q.data?.shifts ?? [])
    .filter((s) => s.user_id === (c.person?.id ?? null) && overlaps(span, s))
    .sort((x, y) => x.start_time.localeCompare(y.start_time));
  return { q, theirs };
}

/** How the deduction is counted, for the case's ⓘ: virkedager or arbeidsdager, whole days. */
export function AbsenceAbout({ c }: { c: AbsenceCase }) {
  const a = c.absence;
  const charge = useDeductionState(
    { absence_type_id: a.typeId, start_date: a.start, end_date: a.end },
    a.deductible,
  );
  if (charge.status !== 'ready' || charge.days == null) return null;
  const basis =
    charge.data?.basis === 'arbeidsdager'
      ? T('Telles i arbeidsdager, mandag til fredag, uten helligdager.')
      : charge.data?.basis
        ? T('Telles i virkedager, mandag til lørdag, uten helligdager.')
        : '';
  const whole =
    a.kind !== 'date' ? T('Denne fraværstypen trekker hele dager, selv om fraværet varer noen timer.') : '';
  return <>{[basis, whole].filter(Boolean).join(' ')}</>;
}

export function AbsenceImpact({ c, onShift }: { c: AbsenceCase; onShift: (s: ApiShift) => void }) {
  const a = c.absence;
  const userId = c.person?.id ?? null;
  const from = oslo(a.start)?.date ?? '';
  const to = oslo(a.end)?.date ?? from;
  const name = c.person?.name ?? T('Den ansatte');
  const charge = useDeductionState(
    { absence_type_id: a.typeId, start_date: a.start, end_date: a.end },
    a.deductible,
  );
  const limitsQ = useAbsenceLimits(
    userId != null
      ? { user_id: userId, absence_type_id: a.typeId, start_date: a.start, end_date: a.end, exclude_id: a.id }
      : null,
  );
  // the balance the charge comes out of: this year's, or next year's for an absence then;
  // across a new year, or further ahead, no stored field holds it (AbsenceDrawer reads the same)
  const thisYear = new Date().getFullYear();
  const year = from.slice(0, 4) === to.slice(0, 4) ? Number(from.slice(0, 4)) : null;
  const field =
    year === thisYear
      ? 'vacation_days_balance'
      : year === thisYear + 1
        ? 'next_year_vacation_days_balance'
        : null;
  const userQ = useUser(a.deductible && field ? userId : null);
  const balance = field ? userQ.data?.[field] : undefined;
  const { q: shiftsQ, theirs } = useTheirShifts(c);
  const noteId = useId();

  const deduction = (): ReactNode => {
    if (!a.deductible) return <Chip testid="deduction" value={T('Trekker ikke fra feriesaldoen')} />;
    if (charge.status === 'loading') return <Chip testid="deduction" value={T('Henter trekk …')} />;
    if (charge.status !== 'ready' || charge.days == null)
      return <Chip testid="deduction" warn value={T('Kunne ikke hente trekket')} retry={charge.refetch} />;
    return <Chip testid="deduction" label={T('Trekk')} value={days(charge.days)} />;
  };

  const balanceChip = (): ReactNode => {
    if (!a.deductible) return null;
    if (!field)
      return (
        <Chip
          testid="absence-balance-unknown"
          label={T('Saldo')}
          value={T('ukjent')}
          about={
            year == null
              ? T(
                  'Fraværet går over et årsskifte, og saldoen vises ikke her. Se saldoen i Fravær før du svarer.',
                )
              : T('Saldoen for {year} er ikke kjent ennå. Se saldoen i Fravær før du svarer.', { year })
          }
        />
      );
    if (userQ.isPending)
      return <Chip testid="absence-balance-loading" label={T('Saldo')} value={T('henter …')} />;
    if (userQ.isError || balance == null)
      return (
        <Chip
          testid="absence-balance-failed"
          warn
          label={T('Saldo')}
          value={T('ikke hentet')}
          about={T('Kunne ikke hente saldoen til {name}. Det er ikke sjekket om den dekker fraværet.', {
            name,
          })}
          retry={() => void userQ.refetch()}
        />
      );
    if (charge.days == null) return null;
    const left = balance - charge.days;
    // below zero the chip is the warning; its sentence is the tooltip (owner, 18.10)
    return (
      <Chip
        testid="absence-balance"
        warn={left < 0}
        label={T('Saldo')}
        value={fromTo(balance, left)}
        about={
          left < 0
            ? `${T('Saldoen dekker ikke fraværet.')} ${T(
                '{name} har {now} igjen og trenger {need}. Du kan likevel innvilge.',
                { name, now: days(balance), need: days(charge.days) },
              )}`
            : T('Saldoen går fra {now} til {left}.', { now: days(balance), left: days(left) })
        }
      />
    );
  };

  const limits = (): ReactNode => {
    if (userId == null) return null;
    if (limitsQ.isPending) return <Chip testid="absence-limits-loading" value={T('Sjekker grensene …')} />;
    if (limitsQ.isError)
      return (
        <Chip
          testid="absence-limits-failed"
          warn
          value={T('Grensene ikke sjekket')}
          about={T('Kunne ikke sjekke grensene for fraværstypen.')}
          retry={() => void limitsQ.refetch()}
        />
      );
    const warnings = limitsQ.data?.warnings ?? [];
    return warnings.length ? (
      <Chip
        testid="absence-limits"
        warn
        value={T('Over en grense for {type}', { type: a.type })}
        about={`${warnings.map((w) => w.text).join(' ')} ${T('Du kan likevel innvilge.')}`}
      />
    ) : null;
  };

  const shifts = (): ReactNode => {
    if (shiftsQ.isPending) return <Chip testid="absence-shifts-loading" value={T('Henter vaktene …')} />;
    if (shiftsQ.isError)
      return (
        <Chip
          testid="absence-shifts-failed"
          warn
          value={T('Vaktene ikke hentet')}
          about={T('Kunne ikke hente vaktene i perioden. Se dem i planen før du svarer.')}
          retry={() => void shiftsQ.refetch()}
        />
      );
    if (theirs.length === 0)
      return <Chip testid="absence-shifts-none" value={T('Ingen vakter i perioden')} />;
    // approving leaves the shifts as they are (admin absenceRequestController, acceptAbsenceRequest)
    const note =
      theirs.length === 1
        ? T('{name} står på 1 vakt i perioden. Den blir stående om du innvilger; gi den bort i planen.', {
            name,
          })
        : T('{name} står på {n} vakter i perioden. De blir stående om du innvilger; gi dem bort i planen.', {
            name,
            n: theirs.length,
          });
    return (
      <Popover.Root>
        <Popover.Trigger asChild>
          <button
            type="button"
            className={css.chip}
            data-testid="absence-shifts-note"
            aria-describedby={noteId}
          >
            {theirs.length === 1 ? T('1 vakt i perioden') : T('{n} vakter i perioden', { n: theirs.length })}
            <span id={noteId} hidden>
              {note}
            </span>{' '}
            ›
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content className={css.selectList} align="start" sideOffset={6}>
            <p style={{ margin: '2px 6px 6px' }}>{note}</p>
            <ul data-testid="absence-shifts">
              {theirs.map((s) => (
                <li key={s.id}>
                  <Popover.Close asChild>
                    <button type="button" onClick={() => onShift(s)}>
                      {fmtD(dateOf(s))} {clock(s.start_time)}–{clock(s.end_time)}
                    </button>
                  </Popover.Close>
                </li>
              ))}
            </ul>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    );
  };

  return (
    <span className={css.impact} data-testid="absence-impact">
      {deduction()}
      {balanceChip()}
      {limits()}
      {shifts()}
    </span>
  );
}
